import store from "@/store";
import {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteLocationRaw
} from "vue-router";
import { useAcessoOrganograma } from "@/modules/organograma/composables/useAcessoOrganograma";
import { useDadosPainelConsultor } from "@/modules/painel-consultor/composables/useDadosPainelConsultor";
import { rotaConsultor, rotaRegional } from "@/modules/painel-consultor/rotas";
import type { NivelPainel } from "@/modules/painel-consultor/types/painelConsultor.types";

const ROTA_SEM_ACESSO: RouteLocationRaw = { name: "401" };
const ROTA_ERRO: RouteLocationRaw = { name: "500" };

/** Regional "Sem regional" da API; serve de provisoria quando os dados nao chegam. */
const SEM_REGIONAL = 0;

const codUsuarioLogado = (): number =>
  Number(store.getters.usuarioLogado?.codUsuario) || 0;

/**
 * Regional do consultor logado, pelos dados do painel (a mesma carga que a
 * pagina usa). Se a carga falhar, fica a da URL, ou "Sem regional" fora da
 * tela do consultor: a tela mostra o erro com "Tentar novamente".
 */
const regionalDoConsultor = async (
  codUsuario: number,
  codRegionalDaUrl: number
): Promise<number | undefined> => {
  const dados = useDadosPainelConsultor();
  await dados.carregar();

  if (!dados.estado.data) {
    return Number.isNaN(codRegionalDaUrl) ? SEM_REGIONAL : codRegionalDaUrl;
  }

  return dados.consultor(codUsuario)?.codRegional;
};

/**
 * Acesso pelo perfil do organograma (modulo organograma), nao pelo `codNivel`:
 * cada perfil abre as telas que a API libera e, abaixo da Diretoria, so o
 * proprio escopo. Diretor abre tudo; Gestor, a regional que gere (codRegional
 * = o proprio codUsuario, regra da API) e os consultores dela; Consultor, so o
 * proprio painel. Tela fora do perfil ou codigo de outra regional/consultor na
 * URL volta para a tela do usuario; sem perfil, 401.
 *
 * A entrada do menu redireciona para a Diretoria, e daqui cada perfil segue
 * para a propria tela.
 */
const painelConsultorGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const codUsuario = codUsuarioLogado();
  if (!codUsuario) return next(ROTA_SEM_ACESSO);

  const acesso = useAcessoOrganograma();
  await acesso.carregar();

  // Sem a resposta nao ha como saber o perfil.
  if (!acesso.estado.successfully) return next(ROTA_ERRO);

  const perfil = acesso.perfil.value;
  if (!perfil) return next(ROTA_SEM_ACESSO);

  const liberada = acesso.telaLiberada(to.meta.telaPainel as NivelPainel);
  const codRegional = Number(to.params.codRegional);
  const codConsultor = Number(to.params.codConsultor);

  if (perfil === "gestor" && (!liberada || codRegional !== codUsuario)) {
    return next(rotaRegional(codUsuario));
  }

  if (perfil === "consultor") {
    const regional = await regionalDoConsultor(codUsuario, codRegional);

    if (regional === undefined) return next(ROTA_SEM_ACESSO);
    if (!liberada || codRegional !== regional || codConsultor !== codUsuario) {
      return next(rotaConsultor(regional, codUsuario));
    }
  }

  if (!liberada) return next(ROTA_SEM_ACESSO);

  next();
};

export { painelConsultorGuard };
